/**
 * Smartsheet-style task row.
 * One <tr> per project × week schedule.
 * Inline editing for planned_workers / working_days.
 * Progress slider appears only when status === "in_progress".
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ClockAlert, Edit3, Trash2, Loader2 } from "lucide-react";
import { STATUS_META, statusOptions, fmtDateShort, calcBudgetNok, fmtNok, NOK_PER_HOUR, autoHours, effectiveHours } from "./constants";
import { StatusPill } from "./atoms";
import WeekDatePicker from "./WeekDatePicker";
import TextPopoverEditor from "./TextPopoverEditor";

export default function TaskRow({
  row, onEditProject, onCarryOver, onDeleteSchedule, onQuickSaveSchedule, onSaveProgress, onSaveStatus, onSaveField, onReorderDropInWeek, onMoveSchedule,
  isSelected = false, onSelect,
  frameStyle,
  columnOrder,
  statusColors = {},
}) {
  const meta = STATUS_META[row.status] || STATUS_META.new_request;
  const overdue = row.is_overdue;
  const overdueTint = overdue ? "border-l-4 border-l-red-500" : "";
  const customColor = statusColors[row.status];
  // When user set a custom hex, apply as inline background (semi-transparent).
  const rowStyle = customColor ? { backgroundColor: `${customColor}22` } : undefined;
  const rowClasses = `border-b border-white/5 hover:bg-white/[0.03] ${!customColor ? meta.rowTint : ""} ${overdueTint}`;

  // Drag support — allow moving a saved schedule to another week via drop on the week bar,
  // AND allow reordering within the same week by dropping directly onto another row.
  const [dragging, setDragging] = useState(false);
  const [dropHint, setDropHint] = useState(false); // shows a top-border insertion line
  const onRowDragStart = (e) => {
    e.dataTransfer.setData("application/x-capacity-schedule", row.schedule_id);
    e.dataTransfer.setData("application/x-capacity-schedule-week", row.week);
    e.dataTransfer.setData("text/plain", row.schedule_id);
    e.dataTransfer.effectAllowed = "move";
    setDragging(true);
  };
  const onRowDragEnd = () => {
    setDragging(false);
    document.querySelectorAll('[data-testid^="week-group-header-"]').forEach((el) => {
      el.classList.remove("ring-2", "ring-cyan-400", "ring-inset");
    });
  };

  // Row-level drop target: reorder within same week (or fall back to move-across-week).
  const onRowDragOver = (e) => {
    if (row.is_shadow) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDropHint(true);
  };
  const onRowDragLeave = () => setDropHint(false);
  const onRowDrop = (e) => {
    if (row.is_shadow) return;
    e.preventDefault();
    e.stopPropagation();
    setDropHint(false);
    const draggedId = e.dataTransfer.getData("application/x-capacity-schedule");
    const sourceWeek = e.dataTransfer.getData("application/x-capacity-schedule-week");
    if (!draggedId || draggedId === row.schedule_id) return;
    if (sourceWeek && sourceWeek === row.week && onReorderDropInWeek) {
      // Same week → in-week reorder (place dragged before this row)
      onReorderDropInWeek(draggedId, row.schedule_id, row.week);
    } else if (onMoveSchedule) {
      // Cross-week drop → move to this row's week
      onMoveSchedule(draggedId, row.week);
    }
  };

  // Inline-edit state for planned_workers and working_days
  const [editingField, setEditingField] = useState(null);
  const [tempWorkers, setTempWorkers] = useState(row.planned_workers);
  const [tempDays, setTempDays] = useState(row.working_days);
  const [tempHours, setTempHours] = useState(row.total_hours ?? "");
  const [editingStatus, setEditingStatus] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  // Click-to-edit for Task Name + Comments (drives PATCH /projects/{id}/fields)
  const [editingText, setEditingText] = useState(null); // "name" | "comments" | null
  const [tempText, setTempText] = useState("");
  useEffect(() => { setTempWorkers(row.planned_workers); }, [row.planned_workers]);
  useEffect(() => { setTempDays(row.working_days); }, [row.working_days]);
  useEffect(() => { setTempHours(row.total_hours ?? ""); }, [row.total_hours]);

  const commitField = async (field, value) => {
    const currentVal = field === "name" ? row.project_name : row.comments;
    if ((value ?? "") === (currentVal ?? "")) { setEditingText(null); return; }
    try { await onSaveField?.(row.project_id, { [field]: value }); }
    catch { /* toast upstream */ }
    setEditingText(null);
  };

  const commitStatus = useCallback(async (newStatus) => {
    if (!newStatus || newStatus === row.status) { setEditingStatus(false); return; }
    setSavingStatus(true);
    try { await onSaveStatus?.(row.project_id, newStatus); }
    finally { setSavingStatus(false); setEditingStatus(false); }
  }, [row.status, row.project_id, onSaveStatus]);

  // Click-to-edit date columns (Start / Finish / Revised)
  const [editingDate, setEditingDate] = useState(null); // "start" | "finish" | "revised" | null
  const commitDate = async (field, newIso) => {
    if (!newIso) { setEditingDate(null); return; }
    const start = field === "start" ? newIso : row.expected_start;
    const finish = field === "finish" ? newIso : row.expected_finish;
    // Guard: never allow finish < start
    if (start && finish && finish < start) {
      toast.error("Finish date must be after start date");
      setEditingDate(null);
      return;
    }
    try {
      await onQuickSaveSchedule({
        project_id: row.project_id,
        schedule_id: row.schedule_id,
        week: row.week,
        planned_workers: parseInt(row.planned_workers) || 0,
        working_days: parseFloat(row.working_days) || 0,
        expected_start: field === "start" ? newIso : row.expected_start,
        expected_finish: field === "finish" ? newIso : row.expected_finish,
        revised_expected_finish: field === "revised" ? newIso : row.revised_expected_finish,
        assigned_team_ids: row.assigned_team_ids || [],
        is_tentative: row.is_tentative,
        notes: row.comments,
      });
    } catch { /* toast handled upstream */ }
    setEditingDate(null);
  };

  // Progress slider (only for in_progress). Debounced save.
  const [tempProgress, setTempProgress] = useState(row.progress_percent ?? 0);
  const [savingProgress, setSavingProgress] = useState(false);
  useEffect(() => { setTempProgress(row.progress_percent ?? 0); }, [row.progress_percent]);
  const commitProgress = useCallback(async (val) => {
    const v = Math.max(0, Math.min(100, parseInt(val) || 0));
    if (v === (row.progress_percent ?? 0)) return;
    setSavingProgress(true);
    try { await onSaveProgress?.(row.project_id, v); }
    finally { setSavingProgress(false); }
  }, [row.progress_percent, row.project_id, onSaveProgress]);

  const commit = async (field, value) => {
    const nWorkers = field === "workers" ? parseInt(value) || 0 : parseInt(row.planned_workers) || 0;
    const nDays = field === "days" ? parseFloat(value) || 0 : parseFloat(row.working_days) || 0;
    // Hours: "" means "auto-compute from workers × days × 7.5" (send null to backend)
    let nHours = null;
    if (field === "hours") {
      nHours = value === "" || value == null ? null : (parseFloat(value) || 0);
    } else {
      nHours = row.total_hours ?? null;
    }
    const unchanged =
      nWorkers === parseInt(row.planned_workers) &&
      nDays === parseFloat(row.working_days) &&
      (nHours ?? null) === (row.total_hours ?? null);
    if (unchanged) { setEditingField(null); return; }
    try {
      await onQuickSaveSchedule({
        project_id: row.project_id,
        schedule_id: row.schedule_id,
        week: row.week,
        planned_workers: nWorkers,
        working_days: nDays,
        total_hours: nHours,
        expected_start: row.expected_start,
        expected_finish: row.expected_finish,
        assigned_team_ids: row.assigned_team_ids || [],
        is_tentative: row.is_tentative,
        notes: row.comments,
      });
    } catch { /* toast handled upstream */ }
    setEditingField(null);
  };

  // ============================================================
  // Cell renderers keyed by column id (used for drag-to-reorder)
  // ============================================================
  const cellById = {
    week: (
      <td key="week" className="px-3 py-1 text-xs text-slate-200 font-mono font-black whitespace-nowrap bg-slate-800/60 border-r border-slate-700/40">
        {row.week}
      </td>
    ),
    task: (
      <td key="task" className="px-3 py-1">
        <div className="flex items-center gap-1.5">
          {overdue && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-500/20 text-red-200 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest whitespace-nowrap"
                  title={`Overdue by ${row.days_overdue} days`}>
              <AlertTriangle className="w-3 h-3"/> Overdue {row.days_overdue}d
            </span>
          )}
          {row.carryover_from && (
            <span className="inline-flex items-center gap-1 rounded-full bg-purple-500/20 text-purple-200 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest whitespace-nowrap"
                  title={`Moved from ${row.carryover_from}`}
                  data-testid={`carryover-badge-${row.schedule_id}`}>
              ↻ From {row.carryover_from.split("-W")[1]}
            </span>
          )}
          {row.is_shadow && (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/20 text-sky-200 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest whitespace-nowrap"
                  title={`Project spans ${row.expected_start} → ${row.expected_finish}`}
                  data-testid={`shadow-badge-${row.schedule_id}`}>
              ⇔ Spans
            </span>
          )}
          <div className="flex-1 min-w-0 relative">
            {editingText === "name" ? (
              <TextPopoverEditor value={row.project_name || ""}
                                 label="Task name"
                                 placeholder="Task name…"
                                 minRows={1} maxRows={4}
                                 width={520}
                                 testId={`name-editor-${row.schedule_id}`}
                                 onSave={(v) => commitField("name", v.trim())}
                                 onCancel={() => setEditingText(null)}/>
            ) : (
              <button type="button"
                      onClick={() => { setTempText(row.project_name || ""); setEditingText("name"); }}
                      className="text-xs font-black text-slate-100 leading-tight hover:text-cyan-200 focus:outline-none focus:ring-1 focus:ring-cyan-400 rounded px-1 -mx-1 text-left"
                      data-testid={`name-cell-${row.schedule_id}`}
                      title="Click to rename">
                {row.project_name}
              </button>
            )}
            {row.address && <div className="text-[10px] text-slate-400 leading-tight px-1 -mx-1">{row.address}</div>}
          </div>
        </div>
      </td>
    ),
    start: (
      <td key="start" className="px-2 py-1 text-xs whitespace-nowrap w-14 text-center relative">
        {editingDate === "start" ? (
          <WeekDatePicker value={row.expected_start}
                          openOnMount
                          testId={`inline-start-${row.schedule_id}`}
                          maxDate={row.expected_finish || null}
                          onChange={(iso) => commitDate("start", iso)}/>
        ) : (
          <button type="button" onClick={() => setEditingDate("start")}
                  className="w-full px-1 py-0.5 rounded hover:bg-cyan-500/10 text-slate-300 font-mono focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  data-testid={`start-cell-${row.schedule_id}`}
                  title="Click to change start date">
            {fmtDateShort(row.expected_start)}
          </button>
        )}
      </td>
    ),
    finish: (
      <td key="finish" className="px-2 py-1 text-xs whitespace-nowrap w-14 text-center relative">
        {editingDate === "finish" ? (
          <WeekDatePicker value={row.expected_finish}
                          openOnMount
                          testId={`inline-finish-${row.schedule_id}`}
                          minDate={row.expected_start || null}
                          onChange={(iso) => commitDate("finish", iso)}/>
        ) : (
          <button type="button" onClick={() => setEditingDate("finish")}
                  className="w-full px-1 py-0.5 rounded hover:bg-cyan-500/10 text-slate-300 font-mono focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  data-testid={`finish-cell-${row.schedule_id}`}
                  title="Click to change finish date">
            {fmtDateShort(row.expected_finish)}
          </button>
        )}
      </td>
    ),
    members: (
      <td key="members" className="px-2 py-1 w-16 text-xs text-center font-mono">
        {editingField === "workers" ? (
          <input type="number" min="0" autoFocus value={tempWorkers}
                 onChange={(e) => setTempWorkers(e.target.value)}
                 onBlur={() => commit("workers", tempWorkers)}
                 onKeyDown={(e) => {
                   if (e.key === "Enter") commit("workers", tempWorkers);
                   if (e.key === "Escape") { setTempWorkers(row.planned_workers); setEditingField(null); }
                 }}
                 className="w-11 px-1 py-0.5 rounded bg-cyan-500/10 border border-cyan-400/50 text-slate-100 text-center focus:outline-none"
                 data-testid={`inline-workers-${row.schedule_id}`}/>
        ) : (
          <button type="button" onClick={() => setEditingField("workers")}
                  className="w-11 px-1 py-0.5 rounded hover:bg-cyan-500/10 text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  data-testid={`workers-cell-${row.schedule_id}`}
                  title="Number of team members — click to edit">
            {row.planned_workers}
          </button>
        )}
      </td>
    ),
    days: (
      <td key="days" className="px-2 py-1 w-14 text-xs text-center font-mono">
        {editingField === "days" ? (
          <input type="number" step="0.5" min="0" autoFocus value={tempDays}
                 onChange={(e) => setTempDays(e.target.value)}
                 onBlur={() => commit("days", tempDays)}
                 onKeyDown={(e) => {
                   if (e.key === "Enter") commit("days", tempDays);
                   if (e.key === "Escape") { setTempDays(row.working_days); setEditingField(null); }
                 }}
                 className="w-11 px-1 py-0.5 rounded bg-cyan-500/10 border border-cyan-400/50 text-slate-100 text-center focus:outline-none"
                 data-testid={`inline-days-${row.schedule_id}`}/>
        ) : (
          <button type="button" onClick={() => setEditingField("days")}
                  className="w-11 px-1 py-0.5 rounded hover:bg-cyan-500/10 text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-400"
                  data-testid={`days-cell-${row.schedule_id}`}
                  title="Click to edit">
            {typeof row.working_days === "number" ? row.working_days.toFixed(1) : row.working_days || "—"}
          </button>
        )}
      </td>
    ),
    hours: (
      <td key="hours" className="px-2 py-1 w-16 text-xs text-center font-mono">
        {editingField === "hours" ? (
          <input type="number" step="0.5" min="0" autoFocus value={tempHours}
                 onChange={(e) => setTempHours(e.target.value)}
                 onBlur={() => commit("hours", tempHours)}
                 onKeyDown={(e) => {
                   if (e.key === "Enter") commit("hours", tempHours);
                   if (e.key === "Escape") { setTempHours(row.total_hours ?? ""); setEditingField(null); }
                 }}
                 placeholder={`${(autoHours(row.planned_workers, row.working_days) || 0).toFixed(1)} auto`}
                 className="w-14 px-1 py-0.5 rounded bg-cyan-500/10 border border-cyan-400/50 text-slate-100 text-center focus:outline-none"
                 data-testid={`inline-hours-${row.schedule_id}`}/>
        ) : (
          <button type="button" onClick={() => setEditingField("hours")}
                  className={`w-14 px-1 py-0.5 rounded hover:bg-cyan-500/10 focus:outline-none focus:ring-1 focus:ring-cyan-400 ${row.total_hours != null ? "text-slate-200 font-semibold" : "text-slate-400 italic"}`}
                  data-testid={`hours-cell-${row.schedule_id}`}
                  title={row.total_hours != null
                    ? `Manual override: ${row.total_hours}h (click to change)`
                    : `Auto: ${row.planned_workers || 0} × ${row.working_days || 0} × 7.5h (click to override)`}>
            {effectiveHours(row).toFixed(1)}
          </button>
        )}
      </td>
    ),
    budget: (
      <td key="budget" className="px-2 py-1 w-24 text-xs text-emerald-300 text-center font-mono font-black whitespace-nowrap"
          title={`Budget = ${effectiveHours(row).toFixed(1)}h × ${NOK_PER_HOUR} NOK`}
          data-testid={`budget-cell-${row.schedule_id}`}>
        {fmtNok(calcBudgetNok(row.planned_workers, row.working_days, row.total_hours))} <span className="text-emerald-500/70 text-[9px] font-black">NOK</span>
      </td>
    ),
    status: (
      <td key="status" className="px-3 py-1">
        {editingStatus ? (
          <select autoFocus
                  defaultValue={row.status}
                  onChange={(e) => commitStatus(e.target.value)}
                  onBlur={() => setEditingStatus(false)}
                  disabled={savingStatus}
                  className="px-2 py-1 rounded bg-cyan-500/10 border border-cyan-400/50 text-slate-100 text-[11px] focus:outline-none"
                  data-testid={`status-select-${row.schedule_id}`}>
            {statusOptions(row.status).map((s) => (
              <option key={s} value={s} className="bg-slate-900">{STATUS_META[s].label}</option>
            ))}
          </select>
        ) : (
          <button type="button"
                  onClick={() => setEditingStatus(true)}
                  disabled={savingStatus}
                  className="focus:outline-none focus:ring-1 focus:ring-cyan-400 rounded"
                  data-testid={`status-pill-${row.schedule_id}`}
                  title="Click to change status">
            <StatusPill status={row.status}/>
            {savingStatus && <Loader2 className="inline w-3 h-3 ml-1 animate-spin text-cyan-300"/>}
          </button>
        )}
        {row.status === "in_progress" && (
          <div className="mt-1 flex items-center gap-1.5" data-testid={`progress-row-${row.schedule_id}`}>
            <input type="range" min="0" max="100" step="5"
                   value={tempProgress}
                   onChange={(e) => setTempProgress(parseInt(e.target.value))}
                   onMouseUp={(e) => commitProgress(e.target.value)}
                   onKeyUp={(e) => (e.key === "Enter" || e.key === " ") && commitProgress(tempProgress)}
                   onTouchEnd={() => commitProgress(tempProgress)}
                   disabled={savingProgress}
                   className="flex-1 h-1 accent-emerald-400 cursor-pointer"
                   data-testid={`progress-slider-${row.project_id}`}
                   title={`Progress: ${tempProgress}%`}/>
            <span className={`text-[10px] font-mono font-black w-8 text-right ${tempProgress >= 100 ? "text-emerald-300" : tempProgress >= 50 ? "text-cyan-200" : "text-slate-300"}`}
                  data-testid={`progress-value-${row.project_id}`}>
              {savingProgress ? "…" : `${tempProgress}%`}
            </span>
          </div>
        )}
      </td>
    ),
    team: (
      <td key="team" className="px-3 py-1 text-[11px] text-slate-300 max-w-[140px] truncate"
          title={row.assigned_team || row.assigned_team_ids?.join(", ")}>
        {row.assigned_team || (row.assigned_team_ids?.length ? `${row.assigned_team_ids.length} team(s)` : "—")}
      </td>
    ),
    comments: (
      <td key="comments" className="px-3 py-1 text-[11px] text-slate-300 min-w-[280px] whitespace-normal break-words relative">
        {editingText === "comments" ? (
          <TextPopoverEditor value={row.comments || ""}
                             label="Comments"
                             placeholder="Describe the job — full text is visible here…"
                             minRows={4} maxRows={16}
                             width={560}
                             testId={`comments-editor-${row.schedule_id}`}
                             onSave={(v) => commitField("comments", v)}
                             onCancel={() => setEditingText(null)}/>
        ) : (
          <button type="button"
                  onClick={() => { setTempText(row.comments || ""); setEditingText("comments"); }}
                  className="w-full text-left hover:text-cyan-200 focus:outline-none focus:ring-1 focus:ring-cyan-400 rounded px-1 -mx-1"
                  data-testid={`comments-cell-${row.schedule_id}`}
                  title={row.comments || "Click to add comment"}>
            {row.comments || <span className="italic text-slate-500">— click to add</span>}
          </button>
        )}
      </td>
    ),
    actions: (
      <td key="actions" className="px-3 py-1 text-xs whitespace-nowrap">
        <div className="flex items-center gap-1 justify-end">
          {overdue && (
            <button onClick={() => onCarryOver(row.project_id)}
                    className="p-1.5 rounded text-amber-300 hover:bg-amber-500/10"
                    data-testid={`carry-over-${row.schedule_id}`}
                    title="Update carry-over plan">
              <ClockAlert className="w-3.5 h-3.5"/>
            </button>
          )}
          <button onClick={() => onEditProject(row.project_id)}
                  className="p-1.5 rounded text-slate-400 hover:text-cyan-300"
                  data-testid={`edit-project-${row.project_id}`}
                  title="Edit project">
            <Edit3 className="w-3.5 h-3.5"/>
          </button>
          <button onClick={() => onDeleteSchedule(row.schedule_id)}
                  className="p-1.5 rounded text-slate-400 hover:text-red-300"
                  data-testid={`del-sched-${row.schedule_id}`}
                  title="Remove from this week">
            <Trash2 className="w-3.5 h-3.5"/>
          </button>
        </div>
      </td>
    ),
  };

  const order = columnOrder && columnOrder.length ? columnOrder : Object.keys(cellById);

  return (
    <tr className={`${rowClasses} ${dragging ? "opacity-40" : ""} ${row.is_shadow ? "opacity-50 cursor-default" : "cursor-grab"} ${dropHint ? "shadow-[inset_0_3px_0_0_#22d3ee]" : ""} ${isSelected ? "ring-2 ring-inset ring-cyan-400 bg-cyan-500/5" : ""}`}
        draggable={!row.is_shadow}
        onDragStart={row.is_shadow ? undefined : onRowDragStart}
        onDragEnd={row.is_shadow ? undefined : onRowDragEnd}
        onDragOver={row.is_shadow ? undefined : onRowDragOver}
        onDragLeave={row.is_shadow ? undefined : onRowDragLeave}
        onDrop={row.is_shadow ? undefined : onRowDrop}
        onClick={row.is_shadow || !onSelect ? undefined : () => onSelect(row.schedule_id)}
        style={{ ...(rowStyle || {}), ...(frameStyle || {}) }} data-testid={`task-row-${row.schedule_id}`}>
      {order.map((id) => cellById[id]).filter(Boolean)}
    </tr>
  );
}
